import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  Button,
  Space,
  Upload,
  Image,
  message,
  Row,
  Col,
  Tag,
} from 'antd';
import { InboxOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import {
  getSticker,
  createSticker,
  updateSticker,
  getCategoryList,
  uploadFile,
} from '../services/api';

const { TextArea } = Input;
const { Option } = Select;
const { Dragger } = Upload;

const StickerForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState('');

  useEffect(() => {
    loadCategories();
    if (isEdit) {
      loadSticker();
    }
  }, [id]);

  const loadCategories = async () => {
    try {
      const data = await getCategoryList();
      setCategories(data || []);
    } catch (error) {
      console.error('加载分类失败', error);
    }
  };

  const loadSticker = async () => {
    setLoading(true);
    try {
      const data = await getSticker(id);
      form.setFieldsValue({
        name: data.name,
        description: data.description,
        file_type: data.file_type,
        is_vip_only: data.is_vip_only === 1,
        is_featured: data.is_featured === 1,
        status: data.status === 1,
        sort_order: data.sort_order,
        category_ids: data.categories?.map((c) => c.id) || [],
      });
      setUploadedFile({
        url: data.file_url,
        thumbnail_url: data.thumbnail_url,
        file_type: data.file_type,
        file_format: data.file_format,
        file_size: data.file_size,
      });
      setKeywords(data.keywords?.map((k) => k.name) || []);
    } catch (error) {
      message.error('加载贴纸失败');
      navigate('/stickers');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const data = await uploadFile(file);
      setUploadedFile({
        url: data.url,
        file_type: data.file_type,
        file_format: data.format,
        file_size: data.size,
      });
      form.setFieldValue('file_type', data.file_type);
      message.success('上传成功');
    } catch (error) {
      message.error('上传失败');
    } finally {
      setUploading(false);
    }
    return false; // 阻止默认上传
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !keywords.includes(keywordInput.trim())) {
      setKeywords([...keywords, keywordInput.trim()]);
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setKeywords(keywords.filter((k) => k !== keyword));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!uploadedFile?.url) {
        message.error('请上传贴纸文件');
        return;
      }

      const data = {
        name: values.name,
        description: values.description || '',
        file_url: uploadedFile.url,
        thumbnail_url: uploadedFile.thumbnail_url || uploadedFile.url,
        file_type: values.file_type,
        file_format: uploadedFile.file_format,
        file_size: uploadedFile.file_size,
        is_vip_only: values.is_vip_only ? 1 : 0,
        is_featured: values.is_featured ? 1 : 0,
        status: values.status ? 1 : 0,
        sort_order: values.sort_order || 0,
        category_ids: values.category_ids || [],
        keywords: keywords,
      };

      setLoading(true);

      if (isEdit) {
        await updateSticker(id, data);
        message.success('贴纸已更新');
      } else {
        await createSticker(data);
        message.success('贴纸已创建');
      }

      navigate('/stickers');
    } catch (error) {
      console.error('保存失败', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/stickers')}>
            返回
          </Button>
          <h2 style={{ margin: 0 }}>{isEdit ? '编辑贴纸' : '添加贴纸'}</h2>
        </Space>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <Card title="基本信息" bordered={false}>
            <Form
              form={form}
              layout="vertical"
              initialValues={{
                file_type: 'static',
                is_vip_only: false,
                is_featured: false,
                status: true,
                sort_order: 0,
              }}
            >
              <Form.Item
                name="name"
                label="贴纸名称"
                rules={[{ required: true, message: '请输入贴纸名称' }]}
              >
                <Input placeholder="请输入贴纸名称" maxLength={200} />
              </Form.Item>

              <Form.Item name="description" label="贴纸描述">
                <TextArea rows={4} placeholder="请输入贴纸描述（可选）" maxLength={1000} />
              </Form.Item>

              <Form.Item
                name="category_ids"
                label="所属分类"
                rules={[{ required: true, message: '请选择至少一个分类' }]}
              >
                <Select mode="multiple" placeholder="请选择分类（可多选）">
                  {categories.map((cat) => (
                    <Option key={cat.id} value={cat.id}>
                      {cat.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="关键字">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space>
                    <Input
                      placeholder="输入关键字后按回车添加"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onPressEnter={handleAddKeyword}
                      style={{ width: 200 }}
                    />
                    <Button onClick={handleAddKeyword}>添加</Button>
                  </Space>
                  <div>
                    {keywords.map((keyword) => (
                      <Tag
                        key={keyword}
                        closable
                        onClose={() => handleRemoveKeyword(keyword)}
                        style={{ marginBottom: 4 }}
                      >
                        {keyword}
                      </Tag>
                    ))}
                  </div>
                </Space>
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="file_type"
                    label="文件类型"
                    rules={[{ required: true, message: '请选择文件类型' }]}
                  >
                    <Select>
                      <Option value="static">静态图</Option>
                      <Option value="animated">动图</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="sort_order" label="排序权重">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="status" label="上架状态" valuePropName="checked">
                    <Switch checkedChildren="上架" unCheckedChildren="下架" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="is_vip_only" label="VIP专属" valuePropName="checked">
                    <Switch checkedChildren="是" unCheckedChildren="否" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="is_featured" label="精选推荐" valuePropName="checked">
                    <Switch checkedChildren="是" unCheckedChildren="否" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="贴纸文件" bordered={false}>
            <Dragger
              accept="image/*"
              showUploadList={false}
              beforeUpload={handleUpload}
              disabled={uploading}
            >
              {uploadedFile?.url ? (
                <div style={{ padding: 16 }}>
                  <Image
                    src={uploadedFile.url}
                    alt="preview"
                    style={{ maxWidth: '100%', maxHeight: 200 }}
                    preview={false}
                  />
                  <p style={{ marginTop: 8, color: '#666' }}>点击或拖拽重新上传</p>
                </div>
              ) : (
                <div style={{ padding: 24 }}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined style={{ fontSize: 48, color: '#667eea' }} />
                  </p>
                  <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
                  <p className="ant-upload-hint">支持 PNG、JPG、GIF、WebP 格式</p>
                </div>
              )}
            </Dragger>

            {uploadedFile && (
              <div style={{ marginTop: 16 }}>
                <p>
                  <strong>文件格式：</strong>
                  {uploadedFile.file_format?.toUpperCase()}
                </p>
                <p>
                  <strong>文件大小：</strong>
                  {(uploadedFile.file_size / 1024).toFixed(2)} KB
                </p>
                <p>
                  <strong>文件类型：</strong>
                  <Tag color={uploadedFile.file_type === 'animated' ? 'purple' : 'cyan'}>
                    {uploadedFile.file_type === 'animated' ? '动图' : '静态图'}
                  </Tag>
                </p>
              </div>
            )}
          </Card>

          <Card bordered={false} style={{ marginTop: 16 }}>
            <Space style={{ width: '100%' }} direction="vertical">
              <Button
                type="primary"
                block
                size="large"
                loading={loading}
                onClick={handleSubmit}
              >
                {isEdit ? '保存修改' : '创建贴纸'}
              </Button>
              <Button block size="large" onClick={() => navigate('/stickers')}>
                取消
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default StickerForm;

